/**
 * ProgressTimeline Component
 * Chronological timeline of goal progress entries
 * BP-06-03: Care Team & Goals Frontend
 */

import React from 'react';
import { format } from 'date-fns';
import { TrendingUp, TrendingDown, Minus, User, Clock, FileText, Trash2 } from 'lucide-react';
import type { GoalProgressEntry, GoalMeasurementType } from '@/features/goals/types/goalsTypes';
import { formatProgressValue } from '@/features/goals/api/goalsApi';

interface ProgressTimelineProps {
  entries: GoalProgressEntry[];
  measurementType: GoalMeasurementType;
  onDeleteEntry?: (entryId: string) => void;
  isLoading?: boolean;
}

function ProgressTimelineSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex gap-4 animate-pulse">
          <div className="w-10 h-10 bg-cream-dark rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-cream-dark rounded w-1/4 mb-2" />
            <div className="h-3 bg-cream-dark rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function getTrendIcon(current: number, previous: number | null) {
  if (previous === null) return <Minus className="w-4 h-4 text-warm-gray" />;
  if (current > previous) return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (current < previous) return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-warm-gray" />;
}

export function ProgressTimeline({
  entries,
  measurementType,
  onDeleteEntry,
  isLoading = false,
}: ProgressTimelineProps) {
  if (isLoading) return <ProgressTimelineSkeleton />;

  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-warm-gray">
        <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p>No progress entries yet</p>
        <p className="text-sm mt-1">Start tracking progress to see the timeline</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-cream-dark" />

      <div className="space-y-6">
        {entries.map((entry, index) => {
          const previousEntry = index < entries.length - 1 ? entries[index + 1] : null;
          const trendIcon = getTrendIcon(entry.percentage, previousEntry?.percentage ?? null);

          return (
            <div key={entry.id} className="relative flex gap-4 pl-2">
              {/* Timeline dot */}
              <div className="relative z-10 w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center">
                {trendIcon}
              </div>

              {/* Content */}
              <div className="flex-1 bg-white rounded-lg border border-warm-gray/10 p-4 hover:shadow-soft transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-warm-gray mb-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{format(new Date(entry.entryDate), 'MMM d, yyyy')}</span>
                      {entry.sessionType && (
                        <>
                          <span>•</span>
                          <span>{entry.sessionType}</span>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-display font-semibold text-navy">
                        {formatProgressValue(entry.value, measurementType)}
                      </span>
                      <span className="text-sm text-warm-gray">
                        ({entry.percentage}% complete)
                      </span>
                    </div>
                  </div>

                  {onDeleteEntry && (
                    <button
                      onClick={() => onDeleteEntry(entry.id)}
                      className="p-1.5 text-warm-gray hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {entry.notes && (
                  <div className="mt-3 pt-3 border-t border-warm-gray/10">
                    <div className="flex items-start gap-2 text-sm text-navy-light">
                      <FileText className="w-4 h-4 mt-0.5 flex-shrink-0 text-warm-gray" />
                      <p>{entry.notes}</p>
                    </div>
                  </div>
                )}

                {entry.enteredBy && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-warm-gray">
                    <User className="w-3 h-3" />
                    <span>Logged by {entry.enteredBy.firstName} {entry.enteredBy.lastName}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProgressTimeline;

